/**
 * RBAC — Role-Based Access Control.
 *
 * Funciones puras que reciben (usuario, ubicaciones) y devuelven decisiones de
 * permisos. Cero acceso a window/storage/store global.
 *
 * Reglas clave:
 *   - Administrador puede TODO.
 *   - Operador tiene un set fijo de acciones permitidas.
 *   - `switch_location` para Operador requiere 2+ GLNs operables.
 *   - Las ubicaciones externas (`externa: true`) NUNCA son operables — son
 *     orígenes mock de transferencias entrantes.
 */

import type { Usuario, Ubicacion } from '../types';

/**
 * Catálogo completo de acciones del sistema, agrupadas por dominio.
 *
 * Las que aparecen en `ACCIONES_OPERADOR` son ejecutables por ambos roles;
 * las que no, son solo Administrador. La vista "Roles" (Seguridad) deriva
 * la matriz de permisos directo desde este catálogo + el set, sin duplicar.
 *
 * Convención: nombres en snake_case, verbo en infinitivo / "ver_*" para lecturas.
 */
export interface AccionCatalogo {
  accion: string;
  label: string;
  grupo: 'Operativo' | 'Lectura' | 'Crítica' | 'Administración';
  /** Descripción corta — se muestra como tooltip en la matriz. */
  descripcion: string;
  /** Si es true, requiere lógica adicional (ej. switch_location requiere multi-GLN). */
  condicional?: boolean;
}

export const CATALOGO_ACCIONES: readonly AccionCatalogo[] = [
  // -- Operativo --
  { accion: 'despachar', label: 'Despachar remito', grupo: 'Operativo',
    descripcion: 'Confirmar despacho de un remito ERP (informa al SNT)' },
  { accion: 'recibir', label: 'Recibir transferencia', grupo: 'Operativo',
    descripcion: 'Confirmar recepción de una transferencia entrante' },
  { accion: 'generar_pdf', label: 'Generar PDF', grupo: 'Operativo',
    descripcion: 'Descargar comprobante PDF de trazabilidad' },
  // -- Lectura --
  { accion: 'ver_dashboard', label: 'Ver Dashboard', grupo: 'Lectura',
    descripcion: 'KPIs operativos del GLN activo' },
  { accion: 'ver_series', label: 'Ver Series', grupo: 'Lectura',
    descripcion: 'Inventario completo de series con filtros' },
  { accion: 'ver_trazabilidad', label: 'Ver Trazabilidad', grupo: 'Lectura',
    descripcion: 'Timeline de movimientos de una serie individual' },
  { accion: 'ver_anmat', label: 'Ver Log ANMAT', grupo: 'Lectura',
    descripcion: 'Estado de operaciones en el SNT' },
  { accion: 'ver_reportes', label: 'Ver Reportes', grupo: 'Lectura',
    descripcion: 'Movimientos, cruce ERP↔SNT, destrucciones' },
  { accion: 'ver_maestros_readonly', label: 'Ver Maestros', grupo: 'Lectura',
    descripcion: 'Catálogos read-only (productos, clientes, eventos)' },
  { accion: 'ver_audit_trail', label: 'Ver Audit Trail', grupo: 'Lectura',
    descripcion: 'Registro inmutable GAMP 5 con hash chain' },
  { accion: 'ver_apilog', label: 'Ver Logs API', grupo: 'Lectura',
    descripcion: 'Interacciones con ERP y SNT' },
  { accion: 'ver_seguridad', label: 'Ver Seguridad', grupo: 'Lectura',
    descripcion: 'Usuarios, roles, sesiones, política de sesión' },
  // -- Crítica (Operador permitido, requiere RBAC defensivo + audit) --
  { accion: 'cancelar_transaccion', label: 'Cancelar transacción SNT', grupo: 'Crítica',
    descripcion: 'Cancelar una operación informada/en cola/con error' },
  { accion: 'cancelar_recepcion', label: 'Cancelar recepción', grupo: 'Crítica',
    descripcion: 'Revertir una recepción confirmada (si no hay movs posteriores)' },
  { accion: 'reintentar_anmat', label: 'Reintentar ANMAT', grupo: 'Crítica',
    descripcion: 'Reenviar al SNT una operación con error' },
  // -- Administración (solo Admin) --
  { accion: 'crear_ingreso_manual', label: 'Crear ingreso / alta manual', grupo: 'Administración',
    descripcion: 'Cargar series nuevas vía Ingreso / Alta (sin informar al SNT)' },
  { accion: 'ver_lotes', label: 'Ver lotes', grupo: 'Administración',
    descripcion: 'Visor consolidado de lotes manuales (re-imprimir etiquetas, eliminar)' },
  { accion: 'eliminar_ingreso', label: 'Eliminar lote de ingreso', grupo: 'Administración',
    descripcion: 'Borrar un lote manual completo (solo si no tiene series usadas)' },
  { accion: 'editar_eventos_default', label: 'Editar evento ANMAT default', grupo: 'Administración',
    descripcion: 'Cambiar el evento default que se infiere para cada tupla (informador, origen, destino)' },
  { accion: 'switch_location', label: 'Cambiar GLN activo', grupo: 'Administración', condicional: true,
    descripcion: 'Cambiar el GLN en el que se opera (Operador: solo si tiene 2+ GLNs)' },
  { accion: 'resetear_demo', label: 'Resetear datos demo', grupo: 'Administración',
    descripcion: 'Volver el sistema al estado inicial (solo entorno demo)' },
  { accion: 'editar_politica_sesion', label: 'Editar política de sesión', grupo: 'Administración',
    descripcion: 'Cambiar el timeout de inactividad y warning' },
  { accion: 'forzar_logout_sesion', label: 'Forzar logout remoto', grupo: 'Administración',
    descripcion: 'Cerrar sesiones activas de otros usuarios' },
];

/** Acciones que un Operador puede ejecutar (las administrativas son solo Admin). */
const ACCIONES_OPERADOR = new Set<string>([
  'ver_dashboard',
  'ver_series',
  'despachar',
  'recibir',
  'ver_trazabilidad',
  'ver_anmat',
  'generar_pdf',
  'ver_reportes',
  'ver_maestros_readonly',
  // Defensa en profundidad: acciones críticas que el Operador puede ejecutar
  // pero requieren chequeo defensivo además de la guarda visual.
  'cancelar_transaccion',
  'cancelar_recepcion',
  'reintentar_anmat',
  // NOTA paquete L.1: `crear_ingreso_manual`, `ver_lotes` y `eliminar_ingreso`
  // se restringieron a Admin (por decisión de producto — "por ahora"). Si en
  // el futuro se vuelven a habilitar para Operador, agregarlos acá y revertir
  // los chequeos en las páginas + sidebar.
]);

/**
 * Acciones EXTRA del Gerente de Logística sobre el Operador (paquete W).
 *
 * El Gerente es un rol intermedio entre Operador y Administrador. Tiene todo
 * lo del Operador + estas acciones de visibilidad operativa y configuración
 * de catálogo ANMAT. NO tiene acceso a configuración de seguridad (política
 * de sesión, usuarios, forzar logout) — eso queda Admin.
 */
const ACCIONES_GERENTE_EXTRA = new Set<string>([
  'ver_audit_trail', // auditar sin ser sysadmin
  'ver_apilog', // debugging operativo
  // 'ver_seguridad' restringido a Admin (gestión de usuarios = solo admin).
  'editar_eventos_default', // conoce mejor los flujos que IT
  // NO: editar_politica_sesion, forzar_logout_sesion, resetear_demo
]);

/**
 * Devuelve true/false/'depende' para indicar si el rol tiene permitida la acción.
 * - true: siempre permitido.
 * - false: nunca permitido.
 * - 'depende': permitido bajo condición (ej. switch_location requiere multi-GLN).
 *
 * La vista de Roles usa esto para renderizar la matriz sin tocar reglas internas.
 */
export function permisoDeRol(
  rol: 'Administrador' | 'Gerente de Logística' | 'Operador',
  accion: string,
): true | false | 'depende' {
  if (rol === 'Administrador') return true;
  if (accion === 'switch_location') return 'depende';
  if (rol === 'Gerente de Logística') {
    return ACCIONES_OPERADOR.has(accion) || ACCIONES_GERENTE_EXTRA.has(accion);
  }
  return ACCIONES_OPERADOR.has(accion);
}

/**
 * ¿El usuario puede ejecutar esta acción?
 *
 * - Admin: siempre true.
 * - Gerente de Logística: todo lo de Operador + ACCIONES_GERENTE_EXTRA.
 * - Operador: true si la acción está en el set permitido, con la excepción de
 *   `switch_location` que requiere multi-GLN.
 * - Sin usuario: siempre false.
 */
export function puedeActuar(
  usuario: Usuario | null,
  accion: string,
  ubicaciones: readonly Ubicacion[] = [],
): boolean {
  if (!usuario) return false;
  if (usuario.rol === 'Administrador') return true;
  if (accion === 'switch_location') {
    return getUbicacionesPermitidas(usuario, ubicaciones).length >= 2;
  }
  if (usuario.rol === 'Gerente de Logística') {
    return ACCIONES_OPERADOR.has(accion) || ACCIONES_GERENTE_EXTRA.has(accion);
  }
  return ACCIONES_OPERADOR.has(accion);
}

/**
 * Lista de GLNs en los que el usuario puede operar.
 *
 * Siempre se filtra por los GLN ASIGNADOS al usuario (vía `user_gln` en DB):
 * `ubicacionesIds` + `ubicacionDefaultId` + `ubicacionId` (legacy), deduplicados.
 * Sin distinción por rol — un Admin SÓLO ve los GLN que tiene asignados.
 * Si querés que un Admin pueda operar en otro GLN, hay que asignárselo en
 * `user_gln`.
 *
 * Garantía: las ubicaciones con `externa: true` JAMÁS aparecen en el resultado.
 * Esto previene que un usuario "opere como" un laboratorio o droguería externa
 * (violación de identidad GAMP 5).
 */
export function getUbicacionesPermitidas(
  usuario: Usuario | null,
  ubicaciones: readonly Ubicacion[],
): Ubicacion[] {
  if (!usuario) return [];
  const ids = new Set<string>();
  if (Array.isArray(usuario.ubicacionesIds)) {
    for (const id of usuario.ubicacionesIds) ids.add(id);
  }
  if (usuario.ubicacionDefaultId) ids.add(usuario.ubicacionDefaultId);
  if (usuario.ubicacionId) ids.add(usuario.ubicacionId);
  const byId = new Map(ubicaciones.map((u) => [u.id, u]));
  const result: Ubicacion[] = [];
  for (const id of ids) {
    const u = byId.get(id);
    if (u && !u.externa) result.push(u);
  }
  return result;
}

/** ¿El usuario puede operar en `ubicacionId`? */
export function puedeOperarEnUbicacion(
  usuario: Usuario | null,
  ubicacionId: string,
  ubicaciones: readonly Ubicacion[],
): boolean {
  return getUbicacionesPermitidas(usuario, ubicaciones).some((u) => u.id === ubicacionId);
}

/**
 * GLN por default del usuario — la que se setea como activa al iniciar sesión.
 *
 *   1. Si tiene `ubicacionDefaultId` válido (interno) → esa.
 *   2. Si no, fallback a `ubicacionId` (legacy, también interno).
 *   3. Si ninguno aplica y es Admin → primera ubicación interna disponible.
 *   4. Caso contrario → null (Operador sin GLN asignado: estado inválido).
 */
export function getUbicacionDefaultDeUsuario(
  usuario: Usuario | null,
  ubicaciones: readonly Ubicacion[],
): Ubicacion | null {
  if (!usuario) return null;
  const defaultId = usuario.ubicacionDefaultId ?? usuario.ubicacionId;
  if (defaultId) {
    const ub = ubicaciones.find((u) => u.id === defaultId);
    if (ub && !ub.externa) return ub;
  }
  if (usuario.rol === 'Administrador') {
    return ubicaciones.find((u) => !u.externa) ?? null;
  }
  return null;
}
